import { Button, ButtonProps, FormControlLabel } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import invariant from 'invariant';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BrandHeader } from 'components/BrandHeader';
import { Checkbox } from 'components/Checkbox';
import { Typography } from 'components/Typography';
import { AppFormConfig } from 'features/ApplicationForm';

import AmazonLogo from './logos/amazon.svg';
import AppleLogo from './logos/apple.png';
import GoogleLogo from './logos/google.png';
import IndeedLogo from './logos/indeed.png';
import { Message } from './Messages';

interface AppFormConfigWithSlug extends AppFormConfig {
  account: AppFormConfig['account'] & { slug: string };
}

const useStyles = makeStyles(theme =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      alignItems: 'center',
      marginTop: theme.spacing(4),
    },
    optionList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    logoBox: {
      height: 42,
      width: 42,
      '& img': {
        borderRadius: 8,
      },
    },
    line: {
      border: `1px solid ${theme.palette.common.gray300}`,
      flexGrow: 1,
    },
    dividerBox: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
      width: '100%',
      margin: theme.spacing(2, 0),
    },
    buttonLabel: {
      margin: theme.spacing(0, 3),
    },
  }),
);

const LoginOption: React.FC<Pick<ButtonProps, 'startIcon' | 'onClick'>> = ({
  children,
  ...rest
}) => {
  const styles = useStyles();
  return (
    <Button
      {...rest}
      variant="outlined"
      classes={{ label: styles.buttonLabel }}
    >
      {children}
    </Button>
  );
};

type LoginImageAltType = { [key: string]: string };
type LoginImageSrcType = { [key: string]: string | undefined };

type TwoFactorLoginMethod = 'Phone' | 'Email';
export type SSOLoginMethod = 'Amazon' | 'Apple' | 'Google' | 'Indeed';
type LoginExperimentVariantVariedOrder =
  | `AmazonGoogleIndeed${TwoFactorLoginMethod}_${Exclude<
      SSOLoginMethod,
      'Apple'
    >}`
  | `AmazonGoogleApple${TwoFactorLoginMethod}_${Exclude<
      SSOLoginMethod,
      'Indeed'
    >}`;
type LoginExperimentVariantCombinedSetOrder =
  `GoogleAppleIndeed${TwoFactorLoginMethod}`;
export type LoginExperimentVariant =
  | LoginExperimentVariantVariedOrder
  | LoginExperimentVariantCombinedSetOrder;

const LOGIN_IMAGE_SRC: LoginImageSrcType = {
  Google: GoogleLogo,
  Amazon: AmazonLogo,
  Apple: AppleLogo,
  Indeed: IndeedLogo,
};

const ssoLoginList: SSOLoginMethod[] = ['Amazon', 'Apple', 'Google', 'Indeed'];
const isSSOLoginMethod = (method: string): method is SSOLoginMethod => {
  return ssoLoginList.includes(method as SSOLoginMethod);
};

const comprehendVariant = (
  variant: LoginExperimentVariant,
): [SSOLoginMethod | null, SSOLoginMethod[]] => {
  const [allLoginMethodsFromVariant, maybeFirstLoginMethodFromVariant] =
    variant.split('_');
  const maybeFirstLoginMethod: SSOLoginMethod | null = isSSOLoginMethod(
    maybeFirstLoginMethodFromVariant,
  )
    ? maybeFirstLoginMethodFromVariant
    : null;

  const splitVariants = (
    allLoginMethodsFromVariant.match(/([A-Z][a-z]+)/g) ?? []
  ).filter(
    m =>
      ['Phone', 'Email'].includes(m) === false && m !== maybeFirstLoginMethod,
  );
  invariant(splitVariants, `Failed to comprehend \`variant\`: ${variant}`);

  const ssoLoginMethods: SSOLoginMethod[] | null = splitVariants.every(
    isSSOLoginMethod,
  )
    ? splitVariants
    : null;
  invariant(ssoLoginMethods, `Failed to comprehend \`variant\`: ${variant}`);

  return [maybeFirstLoginMethod, ssoLoginMethods];
};
export interface LoginSelectionProps {
  logoUrl?: string;
  variant: LoginExperimentVariant;
  onLogin?: () => void;
  appFormData: AppFormConfigWithSlug;
}

export const LoginSelection: React.VFC<LoginSelectionProps> = ({
  logoUrl,
  variant,
  onLogin,
  appFormData,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const LOGIN_IMAGE_ALT: LoginImageAltType = {
    Google: intl.formatMessage({
      defaultMessage: 'Google logo',
      description: 'alt text for Google logo',
    }),
    Amazon: intl.formatMessage({
      defaultMessage: 'Amazon logo',
      description: 'alt text for Amazon logo',
    }),
    Apple: intl.formatMessage({
      defaultMessage: 'Apple logo',
      description: 'alt text for Apple logo',
    }),
    Indeed: intl.formatMessage({
      defaultMessage: 'Indeed logo',
      description: 'alt text for Indeed logo',
    }),
  };

  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    }
  };

  const showEmail = variant.includes('Email');

  const [maybeFirstLoginMethod, ssoLoginMethods] = comprehendVariant(variant);

  return (
    <div className={styles.container}>
      <div className={styles.logoBox}>
        {logoUrl && (
          <BrandHeader brandName={appFormData.brand.name} logoUrl={logoUrl} />
        )}
      </div>
      <Typography variant="headingSM">
        <FormattedMessage
          defaultMessage="Authenticate yourself to begin your application"
          description="Prompt shown to user when selecting a login method to verify their contact details"
        />
      </Typography>

      <section className={styles.optionList}>
        {maybeFirstLoginMethod && (
          <LoginOption
            onClick={() => handleLogin()}
            startIcon={
              <img
                alt={LOGIN_IMAGE_ALT[maybeFirstLoginMethod]}
                src={LOGIN_IMAGE_SRC[maybeFirstLoginMethod]}
              />
            }
          >
            <Message method={maybeFirstLoginMethod} />
          </LoginOption>
        )}
        {ssoLoginMethods.map(method => {
          return (
            <LoginOption
              key={method}
              onClick={() => handleLogin()}
              startIcon={
                <img
                  alt={LOGIN_IMAGE_ALT[method]}
                  src={LOGIN_IMAGE_SRC[method]}
                />
              }
            >
              <Message method={method} />
            </LoginOption>
          );
        })}

        <section className={styles.dividerBox}>
          <div className={styles.line} />
          <Typography variant="headingXS" color="gray600">
            <FormattedMessage
              defaultMessage="or"
              description="divider, signifies 'or choose these other options'"
            />
          </Typography>
          <div className={styles.line} />
        </section>

        {showEmail ? (
          <LoginOption onClick={() => handleLogin()}>
            <FormattedMessage
              defaultMessage="Continue with Email"
              description="Button that uses the user email to sign in"
            />
          </LoginOption>
        ) : (
          <LoginOption onClick={() => handleLogin()}>
            <FormattedMessage
              defaultMessage="Text me one-time passcode"
              description="Button that uses SMS or WhatsApp to sign in"
            />
          </LoginOption>
        )}
      </section>
      <FormControlLabel
        control={<Checkbox />}
        label={intl.formatMessage({
          defaultMessage: 'Log me out after this session',
          description:
            'The user clicks this checkbox to be logged out after they close the tab',
        })}
      />
    </div>
  );
};
